import {
	EditToDoAppViewModel,
	SaveToDoAppModel,
	saveToDoApp
} from "client/Api/Apps/EditApp/saveApp";
import { AppType } from "client/Api/Apps/common";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";

const validation = createValidationConfiguration<SaveToDoAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type EditToDoAppProps = {
	initialSettings: EditAppSettings<EditToDoAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditToDoApp = forwardRef(
	(
		props: EditToDoAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveToDoAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveToDoAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveToDoAppModel>(
			formHandler,
			saveToDoApp,
			AppType.ToDo
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);

EditToDoApp.displayName = "EditToDoApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditToDoAppViewModel>
): SaveToDoAppModel {
	const {
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow
	};
}